import React from 'react';
import gql from 'graphql-tag';

import { InputDlg } from './input-dialog';
import { Form as FForm, Formik, useField } from 'formik';
import {
  ButtonSearch,
  FieldInput,
  FormCol6,
  FilterProps,
  promiseModalSelect,
  FormRow,
  TableCellV8,
  FFProps,
  FormFieldModalDlg,
  FormCol,
  CellDateV8,
  CellCenterV8,
  CellDateTimeV8,
  CellFloatCustomV8,
} from 'motion-components';
import { Form, Col } from 'react-bootstrap';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { getRouteByName } from 'app/routes';
import { isFotokiklosi, isRebattery } from 'shared-pages/common/constants';
import { FieldSelectNomos } from '../field-select/fs-nomos';

export const FormFieldIdocDlg = ({
  as = 'div',
  label = 'Έντυπο Αναγνώρισης',
  idFieldName = 'idocId',
  nameFieldName = 'identificationDocument',
  disabled,
  placeholder = '',
  required = false,
  size,
  onChange,
}: FFProps) => {
  const [field, { value }] = useField(idFieldName);
  const path = generatePath(getRouteByName('Idoc'), { id: value ?? 0 });
  const showLabel = value ? <Link to={path}>{label}</Link> : label;

  return (
    <FormFieldModalDlg
      DlgComponent={FieldIdocDlg}
      as={as}
      label={showLabel}
      idFieldName={idFieldName}
      nameFieldName={nameFieldName}
      disabled={disabled}
      onChange={onChange}
      required={required}
    />
  );
};

/* ------------------------------------------
   Select Idoc with Modal Search
   ------------------------------------------ */
type Props = {
  initValueId?: string;
  initValueName?: string;
  onValueChange: (valueId: string, valueName: string) => void;
  required?: boolean;
  initFilters?: {} | (() => {});
  disabled?: boolean;
};

export const FieldIdocDlg = ({
  initValueId = undefined,
  initValueName = '',
  onValueChange,
  required = false,
  initFilters = {},
  disabled = false,
}: Props) => {
  const { appStore } = useRootStore();
  const { t, i18n } = useTranslation();

  const valueChanged = (valueId, valueName) => {
    // επιστρέφει valueId=uknown όταν πατηθεί το Cancel
    if (valueId) onValueChange(valueId, valueName);
  };

  return (
    <InputDlg
      valueId={initValueId}
      valueName={initValueName}
      disable={disabled}
      onDlgClick={async (event) => {
        const filters = typeof initFilters === 'function' ? initFilters() : initFilters;
        const autoFetchData = !!filters?.cstGuid;
        const res = await promiseModalSelectIdoc({
          initFilters: filters,
          appStore,
          autoFetchData,
          t,
        });
        return { newId: res?.id, newName: res?.identificationDocument };
      }}
      onValueChange={valueChanged}
    />
  );
};

// -------------------------------------------------------------------------------
// Modal Select Idoc
// -------------------------------------------------------------------------------

const gqlGetQuery = gql`
  query getIdocs($limit: Int, $offset: Int, $order: String, $where: String) {
    getIdocs(limit: $limit, offset: $offset, order: $order, where: $where) {
      data {
        id
        idocPrefix
        identificationDocument
        dateCollected
        collectionPointName
        colpointAddressLine1
        collectionPointId
        collectorCompanyName
        wastereceptionpointCompanyName
        dateStorage
        vehPlateNumber
        driverFullName
        receptionNetWeight
        netWeight
      }
      recordCount
    }
  }
`;

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values, appStore) => {
  // Φτιάξε το where για το graphql
  let where = {};
  if (values) {
    if (values.searchText) {
      const searchText = '%' + values.searchText + '%';
      const likeObj = { $like: searchText };
      where = {
        ...where,
        $or: [{ code: likeObj }],
      };
    }
  }
  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  const { appStore } = useRootStore();
  return (
    <Formik initialValues={initialValues ? initialValues : { searchText: '' }} onSubmit={onChange}>
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-center">
          <FieldInput
            as={FormCol6}
            name="searchText"
            id="searchText"
            label={'Κωδικός Εντύπου'}
            type="search"
          />
          <Col className="pl-0 pr-0 me-3 ">
            <ButtonSearch size="sm" type="submit" className="float-right mb-2" />
          </Col>
        </FormRow>
      </Form>
    </Formik>
  );
};

// -------------------------------------------------------------------------------

export const promiseModalSelectIdoc = (props) => {
  const t = props.t;
  const columns = [
    {
      id: 'id',
      header: t('ID'),
      columnName: t('ID'),
      accessorKey: 'id',
      orderByField: 'id',
      cell: CellCenterV8,
      size: 5,
    },
    {
      id: 'identificationDocument',
      header: t('Κωδικός Εντύπου'),
      columnName: t('Κωδικός Εντύπου'),
      accessorKey: 'identificationDocument',
      orderByField: 'identificationDocument',
      cell: TableCellV8,
      size: 20,
    },
    {
      id: 'dateCollected',
      header: t('Ημερομηνία Συλλογής'),
      columnName: t('Ημερομηνία Συλλογής'),
      accessorKey: 'dateCollected',
      orderByField: 'dateCollected',
      cell: CellDateTimeV8,
      size: 50,
      meta: { type: 'text' },
    },
    {
      id: 'collectionPointName',
      header: t('Προέλευση Αποβλήτων'),
      columnName: t('Προέλευση Αποβλήτων'),
      accessorKey: 'collectionPointName',
      orderByField: 'collectionPointName',

      cell: ({ getValue, row }) => (
        <div className="text-left">
          <div>{row.original.collectionPointName}</div>
          <div style={{ color: 'gray', fontSize: 'smaller' }}>
            {' '}
            {row.original.colpointAddressLine1}
          </div>
        </div>
      ),
      size: 25,
      meta: { type: 'text' },
    },
    {
      id: 'collectorCompanyName',
      header: t('Συλλέκτης'),
      columnName: t('Συλλέκτης'),
      accessorKey: 'collectorCompanyName',
      orderByField: 'collectorCompanyName',
      cell: TableCellV8,
      size: 30,
      meta: { type: 'text' },
    },
    {
      id: 'vehPlateNumber',
      header: t('Όχημα'),
      columnName: t('Όχημα'),
      accessorKey: 'vehPlateNumber',
      orderByField: 'vehPlateNumber',
      cell: ({ getValue, row }) => (
        <div className="text-left">
          {row.original.vehPlateNumber}
          <div style={{ color: 'gray', fontSize: 'smaller' }}> {row.original.driverFullName}</div>
        </div>
      ),
      size: 20,
    },
    {
      id: 'wastereceptionpointCompanyName',
      header: t('Παραλαβή Αποβλήτων'),
      columnName: t('Παραλαβή Αποβλήτων'),
      accessorKey: 'wastereceptionpointCompanyName',
      orderByField: 'wastereceptionpointCompanyName',
      cell: TableCellV8,
      size: 30,
      meta: { type: 'text' },
    },
    {
      id: 'netWeight',
      header: t('Βάρος Αποβλήτων'),
      columnName: t('Βάρος Αποβλήτων'),
      accessorKey: 'netWeight',
      orderByField: 'netWeight',
      cell: CellFloatCustomV8(8, 1),
      size: 30,
      meta: { type: 'text' },
    },

    {
      id: 'dateStorage',
      header: t('Ημερομηνία Αποθήκευσης'),
      columnName: t('Ημερομηνία Αποθήκευσης'),
      accessorKey: 'dateStorage',
      orderByField: 'dateStorage',
      cell: CellDateTimeV8,
      size: 50,
      meta: { type: 'text' },
    },
  ];
  return promiseModalSelect({
    ...props,
    title: 'Επιλογή Συλλογής',
    columns,
    gqlQuery: gqlGetQuery,
    buildWhereFromFilters: (values) => buildWhereFromFilters(values, props.appStore),
    initFilters: props.initFilters,
    Filters: Filters,
    orderBy: 'identificationDocument',
    size: 'xl',
  });
};
// -------------------------------------------------------------------------------
